/*
* User search view for authenticated users
*/

import '@progress/kendo-ui';
import * as React from 'react';
import * as _ from 'lodash';
import { Grid } from '@progress/kendo-grid-react-wrapper';
import { Row, Button, Well, ButtonToolbar } from 'react-bootstrap';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { connectRequest } from 'redux-query';
import { withRouter } from 'react-router-dom';

import * as Actions from '../actions';
import * as AppComp from '../common/app-component-base';
import * as ReduxQueries from '../queries';
import ApiEndpoints from '../config/apiendpoints';
import SearchInput from '../common/searchInput';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { GridUtilities } from '../common/grid/gridutilities';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';

class UserSearch extends AppComp.AppComponent {
    public gridOptions = GridUtilities.kendoGridOptionsPaging(
        [
            GridUtilities.kendoGridColumnDefinition('username', this.appTextGridColumnHeader('username'), 80, 1, GridUtilities.kendoGridLinkColumnTemplate('username', 'userDetailsHref', this.appText('user-detail-link-title'))),
            GridUtilities.kendoGridColumnDefinition('firstName', this.appTextGridColumnHeader('first-name'), 90, 2),
            GridUtilities.kendoGridColumnDefinition('lastName', this.appTextGridColumnHeader('last-name'), 90, 3),
            GridUtilities.kendoGridColumnDefinition('email', this.appTextGridColumnHeader('email'), 180, 4),
            GridUtilities.kendoGridColumnDefinition('title', this.appTextGridColumnHeader('title'), 120, 5),
            GridUtilities.kendoGridColumnDefinition('userRoles', this.appTextGridColumnHeader('roles'), 200, 6),
            GridUtilities.kendoGridColumnDefinition('active', this.appTextGridColumnHeader('active'), 70, 7, GridUtilities.kendoGridCheckboxColumnTemplate('active')),
            GridUtilities.kendoGridColumnDefinition('locked', this.appTextGridColumnHeader('locked'), 70, 8, GridUtilities.kendoGridCheckboxColumnTemplate('locked')),
            GridUtilities.kendoGridColumnDefinition('lastLoginDate', this.appTextGridColumnHeader('last-login-date'), 100, 9, undefined, { format: '{0:g}' }),
        ],
        this.appText('no-results'), {}, this.appTextPageTitle()
    );

    public constructor(props: any) {
        super(props, 'app.user-search.');
    }

    public extendGridData(records: Array<any>) {
        // Extend search result records with additional dynamic field values for use in grid
        if (!records) { return []; }

        return records.map( r => {
            let record = _.cloneDeep(r);

            record.active = !record.inactive;            
            record.userDetailsHref = NavigationLinks.userDetailObjId(Formatters.getStr(record, '_id'));

            record.firstName = Formatters.getStr(record, 'contact.firstName');
            record.lastName = Formatters.getStr(record, 'contact.lastName');
            record.title = Formatters.getStr(record, 'contact.title');

            record.userRoles = this.appText('roles-none');
            const recordRoles = _.get(record, 'roles', []);
            if (_.size(recordRoles)) {
                const userRoleNames = _.map(recordRoles, (c) => Formatters.getStr(c, 'name'));
                if (_.size(userRoleNames)) {
                    record.userRoles = userRoleNames.join(', ');
                }
            }

            if (_.get(record, 'lastLogin')) {
               record.lastLoginDate = new Date(record.lastLogin);
            }

            return record;
        });
    }

    public handleSelectReportMode(e: any) {                
        const value = e;        
        this.props.dispatch(Actions.searchUsersFilterMode(value));
    }    

    // Renderers
    public renderHeaderButtonToolbar = (filterMode: any) => {
        let buttonProps = {
            bsSize: 'sm',
            bsStyle: 'info',
        };                
        // console.log(`renderHeaderButtonToolbar filterMode ${filterMode}`);

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>                                                                
                                <Button {...buttonProps} active={(filterMode === 1)} onClick={(e) => this.handleSelectReportMode(1)}><i className="fa fa-users" /> {this.appText('filter-option-employees')}</Button>
                                <Button {...buttonProps} active={(filterMode === 2)} onClick={(e) => this.handleSelectReportMode(2)}><i className="fa fa-briefcase" /> {this.appText('filter-option-portal-users')}</Button>
                            </ButtonToolbar>
                        </Col>                        
                    </Row>
                </Well>
            </div>
        );
    }

    public handleSearchFilter(e: any) {
        const keyword = e.target.value;
        this.props.dispatch(Actions.searchUsersKeyword(keyword));
    }

    public render() {
        const { search, ...rest } = this.props;        

        let pageTitle = this.appTextResultsAndPageTitle(0);
        let page = (<div />);
        
        if (!_.isEmpty(search)) {
            const rows = this.extendGridData(_.get(search, 'results', []));
            pageTitle = this.appTextResultsAndPageTitle(_.size(_.get(search, 'results', [])));
            let gridTitle = this.appTextResultsAndPageTitle(rows.length);

            const dataSource = GridUtilities.kendoGridDataSourcePaged(rows, {
                sort: { field: 'username', dir: 'asc' },
            });

            const filterMode = _.get(search, 'filters.filterMode');
        
            switch (filterMode) {
                case 1:
                default: 
                    gridTitle = this.appTextResultsAndPageTitle(rows.length, 'page-title.users', true, 'page-title.employees', true);
                    break;

                case 2:
                    gridTitle = this.appTextResultsAndPageTitle(rows.length, 'page-title.users', true, 'page-title.portal-users', true);
                    break;
            }

            page = (
                <div>
                    <div>
                        {this.renderHeaderButtonToolbar(filterMode)}                
                    </div>

                    <WhiteBoxPageSection title={this.appText('search-results')}>
                        <Row>
                            <Col xs={12}>
                                <form className="form-horizontal" onSubmit={e => { e.preventDefault(); }}>
                                    <div className="form-group">
                                        <label className="col-sm-1 control-label">{this.appText('search-keyword')}</label>
                                        <div className="col-sm-2">
                                            <SearchInput onChange={e => {this.handleSearchFilter(e); }} placeholder={this.appText('enter-keyword')} value={this.props.search.term} />
                                        </div>
                                    </div>
                                </form>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12}>
                                <Grid dataSource={dataSource} {...this.gridOptions} />
                            </Col>
                        </Row>
                    </WhiteBoxPageSection>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = () => ApiEndpoints.getSearchUsersUrl();

const mapPropsToConfigs = (props) => ReduxQueries.Queries.searchUsersRequest(
    getApiUrl(),
    ReduxQueries.PropsAccessToken(props),    
    _.get(props, 'search.term', ''),
    _.get(props, 'search.filters.filterMode'),
    _.get(props, 'search.operator', '')
);

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
        search: {
            operator: 'OR',
            results: state.entities.searchUsersResults,
            term: state.searchFiltersUsers.keyword,
            filters: {
                filterMode: state.searchFiltersUsers.filterMode,                
                operator: 'AND',                
            }
        }
    });
};

const UserSearchContainer = compose<any>(
    connect(mapStateToProps),
    connectRequest(mapPropsToConfigs)
  )(UserSearch);

export default withRouter(UserSearchContainer);



// WEBPACK FOOTER //
// ./src/user-search/index.tsx